import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, input, model } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterModule, UrlTree } from '@angular/router';

import { resolveSignalValue } from '@lvflanders/bar-ng';

import { BarMenuState } from '../../../bar-menu-state.type';
import { BaasMenuItemOptions, BaasMenuItemTag } from '../baas-menu-item-options.type';

const CLASS_SELECTED_ITEM = 'bar-menu-link--selected';

@Component({
    selector: 'baas-layout-menu-list',
    templateUrl: './baas-layout-menu-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, RouterModule, MatIconModule, MatTooltipModule]
})
export class BaasLayoutMenuListComponent {
    readonly #router = inject(Router);
    readonly #sanitizer = inject(DomSanitizer);
    readonly #cdr = inject(ChangeDetectorRef);

    readonly menuLevel = input<number>(0);
    readonly path = input<string>('');
    readonly items = model<BaasMenuItemOptions[]>(undefined);
    readonly parentExpanded = input<boolean>(true);
    readonly menuState = model<BarMenuState>('default');

    protected readonly enrichedItems = computed(() => this.#enrichItems(this.items()));

    constructor() {
        this.#router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
            if (event instanceof NavigationEnd) this.#cdr.detectChanges();
        });
    }

    protected async menuItemClicked(event, item: BaasMenuItemOptions, index) {
        if (!this.#checkDisabled(item)) {
            // auto close menu when in overlay modus and menu item has a link
            if (this.menuState() === 'overlay' && (item.routerCommands || item.routerUrl)) this.menuState.set('closed');

            // if menu item has children, trigger expansion logic
            if (item.children && item.children.length) this.menuItemToggleClicked(event, index);
            if (item.action) {
                const actionResult = item.action();
                if (actionResult instanceof Promise) await actionResult;
            }
            if (item.routerUrl) {
                // when routerUrl is configured we want to use SPA navigation not href navigation
                event.preventDefault();
                const val = await this.#router.navigateByUrl(item.routerUrl);
                if (val) window.scrollTo(0, 0);
            }
        } else return false;
    }

    protected menuItemToggleClicked(event, index) {
        event.stopPropagation();

        const tempItem = Object.assign(this.items()[index]);

        if (!this.#checkDisabled(tempItem)) {
            tempItem.expanded = !tempItem.expanded;

            this.items.update((items) =>
                items.map((mapItem, mapIndex) => {
                    if (mapIndex === index) return Object.assign(mapItem, tempItem);
                    return mapItem;
                })
            );
        }
    }

    protected defineHref(item: BaasMenuItemOptions): string | SafeUrl {
        if (item.href) return this.#sanitizer.bypassSecurityTrustUrl(item.href);
        if (item.routerUrl) return item.routerUrl;
        return null;
    }

    protected defineRouterCommands(item: BaasMenuItemOptions) {
        if (item.routerCommands) return item.routerCommands;
        return '/';
    }

    protected getPath(index) {
        return this.path().length ? `${this.path()}.${index}` : `${index}`;
    }

    protected getLinkClasses(item: BaasMenuItemOptions, index: number): string {
        const menuLevelClasses = this.menuLevel() < 3 ? 'bar-menu-link--level-' + this.menuLevel() : 'bar-menu-link--level-deep';
        let activeItemClasses = '';

        let url: UrlTree = null;

        if (item.routerCommands) {
            const commands = item.routerCommands;
            url = this.#router.createUrlTree(commands);
        } else if (item.routerUrl) {
            url = this.#router.parseUrl(item.routerUrl);
        }
        if (url != null) {
            // pathMatch check wether or not to match full path isActive's "exact" property, default true

            // map UrlTree to string
            const serializedUrl = this.#router.serializeUrl(url);
            // remove queryparams and fragment as it is not needed for url check
            const currentUrl = this.#router.url.split('?')[0].split('#')[0];
            // when pathmatch is prefix we check if the menu item is part of the current url (the start of)
            const active = item.pathMatch && item.pathMatch === 'prefix' ? currentUrl.indexOf(serializedUrl) === 0 : serializedUrl === currentUrl;

            if (active) activeItemClasses = CLASS_SELECTED_ITEM;
        }

        // if the consumer marked the menu item as selected we mark it as selected
        if (item.selected === true) activeItemClasses = CLASS_SELECTED_ITEM;
        // if the consumer explicitly marked it as not selected, make sure it doesn't appear as selected (because of url match)
        else if (item.selected === false && activeItemClasses) activeItemClasses = undefined;

        const clickableClasses = item.href || item.routerCommands || item.routerUrl || item.action ? 'bar-menu-link--clickable' : '';
        let result = `${menuLevelClasses} ${activeItemClasses} ${clickableClasses}`;
        if (item.cssClass) result += ` ${item.cssClass}`;
        return result;
    }

    protected getTagClasses(tag: BaasMenuItemTag) {
        const classes: string[] = [];
        if (tag.class) classes.push(tag.class);
        classes.push(`bar-menu-link_tag--${tag.type || 'no-type'}`);
        return classes;
    }

    protected hasLink(item: BaasMenuItemOptions): boolean {
        return this.#checkDisabled(item) ? false : item.routerCommands != null || this.hasDefaultLink(item);
    }

    protected hasDefaultLink(item: BaasMenuItemOptions): boolean {
        return item.href != null || item.routerUrl != null;
    }

    #checkDisabled(item: BaasMenuItemOptions): boolean {
        return resolveSignalValue(item.disabled);
    }

    #enrichItems(menuItems: BaasMenuItemOptions[]) {
        if (!menuItems) return [];
        return menuItems.map((item) => {
            return {
                ...item,
                children: item.children ? this.#enrichItems(item.children) : [],
                disabled: computed(() => resolveSignalValue(item.disabled)),
                hidden: computed(() => resolveSignalValue(item.hidden))
            };
        });
    }
}
